// src/componentes/PlanDia/ModalRenegociacion.jsx
//
// v1.2 / S3-5: Modal que invoca la Cloud Function generarPropuestaRenegociacion
// y muestra la propuesta + guion conversacional al cobrador. Permite editar
// los montos antes de copiar el guion o registrar la promesa.

import React, { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../Firebase/firebaseconfig";
import { crearPromesaPago } from "../../services/cobranzaOperativaService";
import { copiarPortapapeles } from "../common/BotonesAccionRapida";
import notify from "../common/notify";
import "./ModalRenegociacion.css";

const renegociarCallable = httpsCallable(functions, "generarPropuestaRenegociacion");

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("es-MX", {
    style: "currency", currency: "MXN",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });

const ModalRenegociacion = ({
  deudaId,
  clienteNombre = "",
  onClose,
  onRegistroPromesa = null,
}) => {
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [respuesta, setRespuesta] = useState(null);
  const [editado, setEditado] = useState(null);
  const [registrando, setRegistrando] = useState(false);

  // Cargar propuesta al abrir
  useEffect(() => {
    if (!deudaId) return;
    let cancel = false;
    setCargando(true);
    setError(null);
    renegociarCallable({ deudaId })
      .then((r) => {
        if (cancel) return;
        const data = r.data || {};
        setRespuesta(data);
        setEditado({ ...data.propuesta });
      })
      .catch((e) => {
        if (cancel) return;
        const msg = e?.message || "No se pudo generar la propuesta";
        setError(msg);
      })
      .finally(() => {
        if (!cancel) setCargando(false);
      });
    return () => { cancel = true; };
  }, [deudaId]);

  const guionTexto = () => {
    if (!respuesta?.guion) return "";
    const g = respuesta.guion;
    return [
      "APERTURA:", g.apertura,
      "",
      "CUERPO:", g.cuerpo,
      "",
      "POSIBLE OBJECIÓN:", g.objecionEsperada,
      "",
      "CIERRE:", g.cierre,
    ].join("\n");
  };

  const copiarGuion = () => copiarPortapapeles(guionTexto(), "Guion");

  const registrarComoPromesa = async () => {
    if (!editado) return;
    if (!editado.primerPagoISO || !editado.abonoInicial) {
      notify.error("Faltan datos: primer pago y abono inicial");
      return;
    }
    setRegistrando(true);
    try {
      // Nota estructurada para que el admin pueda aplicar la renegociacion
      // manualmente al cumplirse el abono inicial.
      const notas = [
        `[RENEGOCIACION PENDIENTE DE APLICAR]`,
        `Abono inicial: ${fmtMoney(editado.abonoInicial)}`,
        `Mensualidad NUEVA: ${fmtMoney(editado.mensualidadNueva)}`,
        `Plazo extra: +${editado.plazoExtendidoMeses} meses`,
        `Primer pago: ${editado.primerPagoISO}`,
        ``,
        `APLICAR EN DETALLE DE LA DEUDA AL COBRAR EL ABONO INICIAL:`,
        `1. Confirmar pago del abono inicial`,
        `2. Editar deuda: mensualidad = ${editado.mensualidadNueva}, plazo += ${editado.plazoExtendidoMeses}`,
        `3. Recalcular nextDueDate desde ${editado.primerPagoISO} + 1 mes`,
        ``,
        respuesta?.justificacion ? `Motivo IA: ${respuesta.justificacion}` : "",
      ].filter(Boolean).join("\n").slice(0, 1000);

      await crearPromesaPago({
        deudaId,
        fechaPromesa: editado.primerPagoISO,
        montoPrometido: Number(editado.abonoInicial),
        notas,
      });
      notify.success("Promesa registrada con instrucciones para admin");
      onRegistroPromesa?.();
      onClose?.();
    } catch (e) {
      notify.error(e?.message || "No se pudo registrar la promesa");
    } finally {
      setRegistrando(false);
    }
  };

  return (
    <div className="mr-overlay" onClick={onClose}>
      <div className="mr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mr-head">
          <h2>💬 Sugerir renegociación{clienteNombre ? ` · ${clienteNombre}` : ""}</h2>
          <button className="mr-close" onClick={onClose} title="Cerrar">✕</button>
        </div>

        <div className="mr-body">
          {cargando && (
            <div className="mr-loading">
              <div className="mr-spinner" />
              <p>Analizando deuda y armando propuesta…</p>
            </div>
          )}

          {error && (
            <div className="mr-error">
              <strong>No se pudo generar la propuesta</strong>
              <p>{error}</p>
              <button className="mr-btn mr-btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          )}

          {!cargando && !error && respuesta && editado && (
            <>
              <section className="mr-section">
                <h3>PROPUESTA</h3>
                <div className="mr-grid">
                  <label>
                    <span>Abono inicial</span>
                    <input
                      type="number"
                      value={editado.abonoInicial}
                      onChange={(e) =>
                        setEditado({ ...editado, abonoInicial: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    <span>Mensualidad nueva</span>
                    <input
                      type="number"
                      value={editado.mensualidadNueva}
                      onChange={(e) =>
                        setEditado({ ...editado, mensualidadNueva: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    <span>Plazo extendido (meses)</span>
                    <input
                      type="number"
                      value={editado.plazoExtendidoMeses}
                      onChange={(e) =>
                        setEditado({ ...editado, plazoExtendidoMeses: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    <span>Primer pago</span>
                    <input
                      type="date"
                      value={editado.primerPagoISO}
                      onChange={(e) =>
                        setEditado({ ...editado, primerPagoISO: e.target.value })
                      }
                    />
                  </label>
                </div>
                <div className="mr-comparativo">
                  <div>
                    <small>Recuperación con propuesta</small>
                    <strong className="mr-verde">
                      {fmtMoney(editado.recuperacionEsperadaConPropuesta)}
                    </strong>
                  </div>
                  <div>
                    <small>Recuperación sin propuesta</small>
                    <strong className="mr-gris">
                      {fmtMoney(editado.recuperacionEsperadaSinPropuesta)}
                    </strong>
                  </div>
                </div>
                {respuesta.justificacion && (
                  <p className="mr-justif">
                    <strong>¿Por qué?</strong> {respuesta.justificacion}
                  </p>
                )}
              </section>

              <section className="mr-section">
                <h3>GUION CONVERSACIONAL</h3>
                <div className="mr-guion">
                  <div className="mr-guion-bloque">
                    <strong>Apertura</strong>
                    <p>{respuesta.guion.apertura}</p>
                  </div>
                  <div className="mr-guion-bloque">
                    <strong>Cuerpo</strong>
                    <p>{respuesta.guion.cuerpo}</p>
                  </div>
                  <div className="mr-guion-bloque mr-guion-objecion">
                    <strong>Objeción esperada</strong>
                    <p>{respuesta.guion.objecionEsperada}</p>
                  </div>
                  <div className="mr-guion-bloque">
                    <strong>Cierre</strong>
                    <p>{respuesta.guion.cierre}</p>
                  </div>
                </div>
              </section>

              <div className="mr-acciones">
                <button className="mr-btn mr-btn-secondary" onClick={copiarGuion}>
                  📋 Copiar guion
                </button>
                <button
                  className="mr-btn mr-btn-primary"
                  onClick={registrarComoPromesa}
                  disabled={registrando}
                >
                  {registrando ? "Registrando…" : "✓ Registrar como promesa"}
                </button>
              </div>

              <div className="mr-disclaimer">
                <p style={{ margin: 0 }}>
                  ℹ <strong>Reglas de la empresa</strong>: no se condona capital,
                  no se reducen mensualidades, solo se permite extender plazo.
                  El abono inicial es un primer paso (no liquida).
                </p>
                <p style={{ margin: "8px 0 0 0", color: "#d97706", fontWeight: 600 }}>
                  ⚠ Importante: al registrar como promesa NO se actualizan
                  automáticamente la mensualidad ni el plazo de la deuda. Un
                  administrador debe aplicar el cambio manualmente al cobrarse
                  el abono inicial. La nota de la promesa incluye las
                  instrucciones exactas.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalRenegociacion;
